import type { ApiClient } from '@elite-residence/api-client';
import { useMemo } from 'react';
import { useApiClient } from '../api/ApiClientProvider';
import { ActionRecoveryError } from '../ui/ActionRecoveryError';

// PD-02.19 / PD-11.53: the server remains the authority for payload-bound,
// currently authorized replay. Store no form values, responses or credentials.
// sessionStorage survives reload/navigation and isolates browser tabs. There
// is no timeout that silently turns an unknown outcome into a new operation.
const mutations = new Set<keyof ApiClient>([
  'recordBookingFinance',
  'correctBookingFinance',
  'verifyBookingFinance',
  'withdrawBookingFinance',
  'reverseBookingFinance',
  'recordExpense',
  'correctExpense',
  'verifyExpense',
  'withdrawExpense',
  'reverseExpense',
  'replaceConfirmedExpense',
  'recordSupplierRefund',
]);

// Besides the key and fingerprint, a pending identity keeps two opaque IDs
// from its own request (never form values): the revision a conditional command
// was based on, and the Expense Branch. A committed correction advances the
// revision the form shows, and an archived Branch leaves normal selection, but
// recovery must still send the original request (R2/R3).
interface Pending {
  readonly key: string;
  readonly fingerprint: string;
  readonly expectedRevisionId?: string;
  readonly branchId?: string;
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
function opaqueIds(body: unknown): Pick<Pending, 'expectedRevisionId' | 'branchId'> {
  const { expectedRevisionId, branchId } = (body ?? {}) as { expectedRevisionId?: unknown; branchId?: unknown };
  return {
    ...(typeof expectedRevisionId === 'string' ? { expectedRevisionId } : {}),
    ...(typeof branchId === 'string' ? { branchId } : {}),
  };
}

// The server dedupes proof IDs and binds their sorted set; compare likewise.
function canonical<T extends { readonly body?: unknown }>(request: T): T {
  const body = request.body as { readonly proofVersionIds?: unknown } | undefined;
  if (!body || !Array.isArray(body.proofVersionIds)) return request;
  return { ...request, body: { ...body, proofVersionIds: [...new Set(body.proofVersionIds as string[])].sort() } };
}

const hex = async (bytes: Uint8Array<ArrayBuffer>) =>
  Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), (b) => b.toString(16).padStart(2, '0')).join(
    '',
  );
const retrySlot = (staffId: string, operation: string, path: unknown) =>
  `er-financial-retry-v1:${staffId}:${operation}:${JSON.stringify(path ?? null)}`;
const proofPrefix = (staffId: string, operation: string, path: unknown) =>
  `er-financial-proof-v1:${staffId}:${operation}:${JSON.stringify(path ?? null)}:`;

// Shared by controllers using the same tab storage. Publish before hashing
// (WebCrypto is async), so even a very fast response cannot let a simultaneous
// submit allocate another identity while the second hash is still running.
const active = new WeakMap<Storage, Map<string, { request: string; result: Promise<unknown> }>>();

export function financialRetryClient(client: ApiClient, staffId: string, storage: () => Storage): ApiClient {
  return new Proxy(client, {
    get(target, property, receiver) {
      const original = Reflect.get(target, property, receiver);
      if (!mutations.has(property as keyof ApiClient)) return original;
      return async (
        request: {
          readonly headers: Readonly<Record<string, string>>;
          readonly path?: unknown;
          readonly body?: unknown;
        },
        ...rest: unknown[]
      ) => {
        const { 'idempotency-key': proposedKey, ...headers } = request.headers;
        // Include path, conditional headers and every body field, preserving
        // exact decimal strings, omission/null and array order. This digest is
        // comparison metadata only; it is never used to authorize a request.
        const serialized = JSON.stringify(canonical({ ...request, headers }));
        let store: Storage;
        let sent = request;
        let pending: Pending | undefined;
        let resumed: boolean;
        const slot = retrySlot(staffId, String(property), request.path);
        try {
          store = storage();
        } catch {
          throw new ActionRecoveryError();
        }
        let calls = active.get(store);
        if (!calls) {
          calls = new Map();
          active.set(store, calls);
        }
        const inFlight = calls.get(slot);
        if (inFlight) {
          if (inFlight.request !== serialized) throw new ActionRecoveryError();
          return inFlight.result;
        }
        const work = (async () => {
          await Promise.resolve();
          try {
            try {
              const saved = store.getItem(slot);
              resumed = saved !== null;
              if (saved) {
                pending = JSON.parse(saved) as Pending;
                // Restore the original precondition only from this pending
                // identity; every other field must still match its fingerprint.
                const body = request.body as { readonly expectedRevisionId?: unknown } | undefined;
                if (pending.expectedRevisionId !== undefined && typeof body?.expectedRevisionId === 'string')
                  sent = { ...request, body: { ...body, expectedRevisionId: pending.expectedRevisionId } };
              }
              const fingerprint = await hex(
                new TextEncoder().encode(
                  sent === request ? serialized : JSON.stringify(canonical({ ...sent, headers })),
                ),
              );
              pending ??= { key: proposedKey ?? '', fingerprint, ...opaqueIds(request.body) };
              if (
                !/^web-[0-9a-f-]{36}$/.test(pending.key) ||
                !/^[0-9a-f]{64}$/.test(pending.fingerprint) ||
                (pending.expectedRevisionId !== undefined && !UUID.test(pending.expectedRevisionId)) ||
                (pending.branchId !== undefined && !UUID.test(pending.branchId))
              ) {
                throw new ActionRecoveryError();
              }
              if (pending.fingerprint !== fingerprint) throw new ActionRecoveryError();
              // This synchronous read/check/write occurs before sending, even if
              // the page closes before its first response. Concurrent calls in
              // this tab reuse the saved identity; PostgreSQL serializes claims.
              store.setItem(slot, JSON.stringify(pending));
            } catch {
              // Fail closed if persistence is unavailable or metadata is damaged.
              throw new ActionRecoveryError();
            }
            const response = await original(
              { ...sent, headers: { ...headers, 'idempotency-key': pending.key } },
              ...rest,
            );
            // A denial after an unknown attempt proves nothing about its commit.
            // A first definite rejection, however, permits a corrected new action.
            if (response.status < 400 || (!resumed && response.status < 500 && response.status !== 408)) {
              if (store.getItem(slot) === JSON.stringify(pending)) store.removeItem(slot);
            }
            // Only a success can attach this slot's proofs. A rejected attempt
            // leaves them unattached, so a corrected retry may still cite them.
            if (response.status < 400) forgetProofs(store, proofPrefix(staffId, String(property), request.path));
            if (resumed && response.status >= 400 && response.status < 500 && response.status !== 408) {
              throw new ActionRecoveryError(response.status);
            }
            return response;
          } finally {
            calls.delete(slot);
          }
        })();
        calls.set(slot, { request: serialized, result: work });
        return work;
      };
    },
  });
}

function forgetProofs(store: Storage, prefix: string) {
  try {
    const keys: string[] = [];
    for (let index = 0; index < store.length; index++) {
      const key = store.key(index);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    for (const key of keys) store.removeItem(key);
  } catch {
    // The financial result is already definite; a stale memo only replays an
    // upload for this same slot and is cleared by that slot's next success.
  }
}

// SA-01: a cited proof version is part of the bound financial payload. The
// pending financial slot it is uploaded for (Staff, operation, path) owns the
// upload identity, so re-attaching the same file after a reload makes the
// server replay the original version, under its current authorization,
// instead of creating a new one. Only file digests and opaque keys are stored.
export interface ProofIntent {
  readonly staffId: string;
  readonly operation:
    | 'recordBookingFinance'
    | 'correctBookingFinance'
    | 'recordExpense'
    | 'correctExpense'
    | 'replaceConfirmedExpense'
    | 'recordSupplierRefund';
  readonly path?: unknown;
}
export interface ProofFile {
  readonly kind: string;
  readonly bookingId?: string;
  readonly branchId?: string;
  readonly body: Uint8Array;
  readonly contentType: 'application/pdf' | 'image/jpeg' | 'image/png';
}

export function financialProofUpload(client: ApiClient, intent: ProofIntent, storage: () => Storage) {
  return async (file: ProofFile, proposedKey: string) => {
    let store: Storage;
    let key: string;
    try {
      store = storage();
      const scope = new TextEncoder().encode(
        `${JSON.stringify([file.kind, file.bookingId ?? null, file.branchId ?? null, file.contentType])}\n`,
      );
      const bytes = new Uint8Array(scope.length + file.body.length);
      bytes.set(scope);
      bytes.set(file.body, scope.length);
      const slot = `${proofPrefix(intent.staffId, intent.operation, intent.path)}${await hex(bytes)}`;
      const saved = store.getItem(slot);
      key = saved ? (JSON.parse(saved) as { readonly key: string }).key : proposedKey;
      if (typeof key !== 'string' || !/^web-[0-9a-f-]{36}$/.test(key)) throw new ActionRecoveryError();
      store.setItem(slot, JSON.stringify({ key, ...(file.branchId ? { branchId: file.branchId } : {}) }));
    } catch {
      throw new ActionRecoveryError();
    }
    const headers = { 'idempotency-key': key };
    return file.bookingId
      ? client.uploadBookingFinanceProof({
          path: { bookingId: file.bookingId },
          query: { kind: file.kind },
          headers,
          body: file.body,
          contentType: file.contentType,
        })
      : client.uploadExpenseProof({
          query: file.branchId ? { kind: file.kind, branchId: file.branchId } : { kind: file.kind },
          headers,
          body: file.body,
          contentType: file.contentType,
        });
  };
}

export function useFinancialProofUpload(intent: ProofIntent) {
  const client = useApiClient();
  const { staffId, operation } = intent;
  const path = JSON.stringify(intent.path ?? null);
  return useMemo(
    () => financialProofUpload(client, { staffId, operation, path: JSON.parse(path) }, () => window.sessionStorage),
    [client, staffId, operation, path],
  );
}

// The Branch of this Staff member's unresolved Expense recording in this tab.
// The Expense form offers such a Branch even when archived, so the original
// request can be re-entered; the server still refuses any new Expense there.
export function pendingExpenseBranch(staffId: string, storage: () => Storage): string | null {
  try {
    const store = storage();
    const saved = store.getItem(retrySlot(staffId, 'recordExpense', null));
    const pending = saved ? (JSON.parse(saved) as Pending) : undefined;
    const branchId = pending?.branchId;
    if (
      pending &&
      /^web-[0-9a-f-]{36}$/.test(pending.key) &&
      /^[0-9a-f]{64}$/.test(pending.fingerprint) &&
      typeof branchId === 'string' &&
      UUID.test(branchId)
    )
      return branchId;
    // An upload may commit and lose its response before the Expense is sent.
    // Its opaque Branch metadata also permits exact re-attachment after reload.
    const prefix = proofPrefix(staffId, 'recordExpense', null);
    for (let index = 0; index < store.length; index++) {
      const key = store.key(index);
      if (!key?.startsWith(prefix)) continue;
      const proof = JSON.parse(store.getItem(key) ?? '{}') as Pending;
      if (/^web-[0-9a-f-]{36}$/.test(proof.key) && typeof proof.branchId === 'string' && UUID.test(proof.branchId))
        return proof.branchId;
    }
    return null;
  } catch {
    return null;
  }
}

export function useFinancialClient(staffId: string): ApiClient {
  const client = useApiClient();
  return useMemo(() => financialRetryClient(client, staffId, () => window.sessionStorage), [client, staffId]);
}
